import LegalDoc, { Section, SubHead, List, GameNote } from '@/components/LegalDoc'
import { Link } from 'react-router-dom'
import type { CommunityData } from '@/useCommunity'
import { useLanguage } from '@/localization'

/**
 * /terms — the platform-wide Terms of Service covering uliunai.com and every
 * game subdomain. Game-specific clauses (paid features, IP attribution) are
 * scoped with <GameNote> blocks. Per-game server rules live on each game site;
 * the universal code of conduct lives at /conduct.
 */
export default function TermsOfService({ social }: { social: CommunityData['social'] }) {
  const { t } = useLanguage()

  return (
    <LegalDoc
      eyebrow="The rules of the road"
      title="Terms of"
      accent="Service"
      lastUpdated="June 2026"
      social={social}
      intro={
        <>
          <p>
            {t(
              'By accessing any Uliunai.com website, connecting to our game servers, or interacting with any of our community services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you must not use our Services.',
            )}
          </p>
          <p>
            {t(
              'These Terms form a binding agreement between you ("you," "your," or "player") and the Uliunai.com community administration team. They apply to uliunai.com and all of its game subdomains. Please read them carefully.',
            )}
          </p>
        </>
      }
    >
      <Section title="Description of Service">
        <p>
          Uliunai.com is a community-operated platform hosting game servers and community sites
          across multiple games. Our Services include, but are not limited to:
        </p>
        <List
          items={[
            'Hosting and maintaining dedicated game servers',
            'Providing community websites with server information, news, and resources',
            'Collecting and displaying player statistics, leaderboards, and match histories',
            'Offering server administration tools for authorized administrators',
            'Providing community communication channels and support',
            'Optional paid features such as VIP memberships and donations, where offered',
          ]}
        />
        <p>
          Our Services are provided free of charge unless otherwise stated (such as optional VIP
          memberships). We reserve the right to modify, suspend, or discontinue any aspect of our
          Services at any time without prior notice.
        </p>
      </Section>

      <Section title="Rules of Conduct">
        <p>
          Every player on every Uliunai server must follow our universal{' '}
          <Link to="/conduct" className="font-semibold text-uliunai-red hover:underline">
            Code of Conduct
          </Link>
          . Each game additionally has its own game-specific rules published on that game&rsquo;s
          site. In short, across all our Services:
        </p>
        <SubHead>Fair Play</SubHead>
        <List
          items={[
            'Play fairly and in the spirit of the game.',
            'Do not sabotage your team or grief other players.',
            'Do not abuse game mechanics in ways that harm others’ experience.',
          ]}
        />
        <SubHead>No Cheating or Hacking</SubHead>
        <List
          items={[
            'Cheats, hacks, aimbots, wallhacks, exploits, and any unauthorized software that provides an unfair advantage are strictly prohibited.',
            'Exploiting known bugs or glitches for gain or to disrupt others is not permitted — report bugs to an administrator instead.',
            'Modified clients that alter gameplay are not allowed.',
          ]}
        />
        <SubHead>Respect</SubHead>
        <List
          items={[
            'Treat all players and administrators with respect.',
            'Harassment, hate speech, discrimination, excessive toxicity, and targeted abuse will not be tolerated.',
            'Do not spam, impersonate staff or others, or otherwise disrupt the community.',
            'Follow the instructions of server administrators. Admin decisions are final during gameplay.',
          ]}
        />
      </Section>

      <Section title="Paid Features (VIP & Donations)">
        <p>
          Some games offer optional paid features. Payments are processed securely by Stripe. The
          following terms apply wherever paid features are offered:
        </p>
        <List
          items={[
            <><strong className="text-stone-900">Non-refundable:</strong> payments are non-refundable once the benefit has been activated. By purchasing, you acknowledge and accept this.</>,
            <><strong className="text-stone-900">Benefits subject to change:</strong> the specific benefits may be modified, added, or removed at any time at the administration team&rsquo;s discretion; we will make reasonable efforts to communicate significant changes.</>,
            <><strong className="text-stone-900">Admin discretion:</strong> paid status does not exempt players from the rules. Administrators may enforce rules regardless of status, including revoking benefits for serious violations.</>,
            <><strong className="text-stone-900">Service availability:</strong> paid benefits depend on server availability; we do not guarantee uninterrupted access.</>,
          ]}
        />
        <GameNote game="Killing Floor 1">
          <p>
            VIP memberships and one-time donations are currently offered for Killing Floor 1 only,
            via the KF1 site. Other games may not offer paid features.
          </p>
        </GameNote>
      </Section>

      <Section title="Account Termination and Bans">
        <p>
          We reserve the right to restrict, suspend, or permanently ban any player from our Services
          for violations of these Terms or the rules, or for behavior deemed detrimental to the
          community.
        </p>
        <List
          items={[
            <><strong className="text-stone-900">Warnings:</strong> minor or first-time offenses may result in a warning.</>,
            <><strong className="text-stone-900">Temporary bans:</strong> repeated or moderate violations may result in a temporary ban.</>,
            <><strong className="text-stone-900">Permanent bans:</strong> severe violations such as cheating, hacking, or extreme misconduct may result in an immediate, permanent ban.</>,
            <><strong className="text-stone-900">Appeals:</strong> banned players may appeal through our Discord. Decisions on appeals are final; please provide your account identifier and a clear explanation.</>,
          ]}
        />
        <p>
          Bans may be applied by IP address, game account ID, or other identifying information.
          Attempting to circumvent a ban using alternate accounts, VPNs, or other methods is itself a
          violation and may result in extended penalties.
        </p>
      </Section>

      <Section title="Intellectual Property">
        <p>
          Each game and its assets, trademarks, and related intellectual property belong to their
          respective owners. Killing Floor is the property of Tripwire Interactive, LLC; Minecraft is
          the property of Mojang Studios / Microsoft. Uliunai.com is an independent community project
          and is not affiliated with, endorsed by, or sponsored by these companies.
        </p>
        <p>
          The Uliunai.com website designs, custom tools, server configurations, and original content
          created for this platform are the property of the Uliunai.com community team. You may not
          reproduce, distribute, or create derivative works from our original content without prior
          written permission.
        </p>
        <p>
          User-generated content you submit (such as contact form messages) remains yours; by
          submitting it you grant us a non-exclusive, royalty-free license to use it for the purpose
          of operating our Services.
        </p>
      </Section>

      <Section title="Limitation of Liability">
        <p>
          Our Services are provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis
          without warranties of any kind, express or implied. We do not guarantee:
        </p>
        <List
          items={[
            'Uninterrupted or error-free operation of our game servers or websites',
            'The accuracy, reliability, or completeness of any statistics or information displayed',
            'That our Services will meet your specific requirements or expectations',
            'The security of data transmitted to or from our Services',
          ]}
        />
        <p>
          To the maximum extent permitted by applicable law, the Uliunai.com community team shall not
          be liable for any direct, indirect, incidental, consequential, or special damages arising
          from your use of our Services. As a volunteer-run community project, downtime, maintenance
          windows, and service interruptions may occur without advance notice.
        </p>
      </Section>

      <Section title="Changes, Governing Framework & Severability">
        <p>
          We may modify these Terms at any time; when we do, we will update the &ldquo;Last
          updated&rdquo; date above and may announce significant changes via Discord or our sites.
          Your continued use after changes constitutes acceptance of the revised Terms.
        </p>
        <p>
          Uliunai.com is a community-managed project. These Terms are governed by the principles of
          fair and reasonable community management; disputes are resolved by the administration team,
          and we encourage you to first contact an administrator via Discord. If any provision of
          these Terms is found unenforceable, it shall be limited or removed to the minimum extent
          necessary, and the remaining provisions remain in full force and effect.
        </p>
      </Section>
    </LegalDoc>
  )
}
